import { getShortAddressFromAddress } from "shared/addressUtils";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import { Property } from "types/landlord/address";
import {
  type CellClickedEvent,
  type ColDef,
  type GetRowIdParams,
  type ICellRendererParams
} from "ag-grid-community";
import { Dispatch, FC, SetStateAction, useCallback, useMemo } from "react";

import { getErrorMessage } from "utils/error";
import { getSourceLabel } from "utils/getSourceLabel";
import {
  Action,
  ContextMenuPopover
} from "shared/components/ContextMenuPopover";
import { toast } from "react-toastify";
import { Door } from "providers/Layout/components/Door";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { PartnerFeatures, PropertyKind } from "shared/types";

import {
  AgGrid,
  DEFAULT_AG_GRID_VIEWPORT_OFFSET
} from "shared/components/AgGrid";
import { handleCopy } from "utils/handleCopy";
import { useLayout } from "providers/Layout/hooks";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { Search01Icon } from "@hugeicons-pro/core-stroke-rounded";

type Props = {
  data: Property[];
  setSelectedPropertyId: Dispatch<SetStateAction<string | null>>;
  setShowDeletePropertyModal: (value: boolean) => void;
  hasSearchParams?: boolean;
  isLoading?: boolean;
  showPropertyGroup?: boolean;
  pagination?: React.ReactNode;
  perPageRows?: number;
  viewPortHeightOffset?: number;
};

export const PropertiesTable: FC<Props> = ({
  data,
  setShowDeletePropertyModal,
  setSelectedPropertyId,
  showPropertyGroup = true,
  isLoading = false,
  pagination,
  perPageRows,
  viewPortHeightOffset,
  hasSearchParams
}) => {
  const { partnerFeatures, type } = useAuthContext();
  const hasPropertyOwner = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );

  const { sectionHeight } = useLayout();

  const {
    Unit,
    Units,
    units,
    Property,
    Properties,
    property: propertyDictionary
  } = useUnitsPropertiesDictionary();

  const navigate = useNavigate();

  const columns = useMemo<ColDef<Property>[]>(() => {
    const isBoomscreenAndPms = type.isBoomScreen && type.isPMS;

    const ret: ColDef<Property>[] = [
      {
        headerName: Property,
        field: "address",
        colId: "address",
        pinned: "left",
        flex: 1,
        minWidth: 280,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Property>) => {
          const row = params.data;
          if (!row) return null;

          return (
            <div className="flex flex-col justify-center gap-0.5">
              <p className="text-sm font-medium text-content-1000 truncate">
                {row.name}
              </p>
              <p className="text-sm text-content-400 truncate">
                {getShortAddressFromAddress(row.address)}
              </p>
            </div>
          );
        }
      },
      {
        headerName: Units,
        field: "total_units",
        colId: "total_units",
        width: 124,
        minWidth: 124,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Property>) => {
          const row = params.data;
          if (!row) return null;
          const title = row.total_units > 1 ? Units : Unit;

          if (row.kind === PropertyKind.SingleFamily)
            return <div>Single family</div>;

          return (
            <div className="flex items-center gap-2">
              <Door className="shrink-0 stroke-content-500" size="20" />
              <p className="text-content-500">
                {row.total_units + " " + title}
              </p>
            </div>
          );
        }
      }
    ];

    if (showPropertyGroup)
      ret.push({
        headerName: "Property group",
        field: "property_group.name",
        colId: "property_group.name",
        width: 180,
        minWidth: 180,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Property>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <p className="text-content-500 font-medium truncate">
              {row.property_group?.name || "--"}
            </p>
          );
        }
      });

    if (hasPropertyOwner)
      ret.push({
        headerName: "Owner",
        field: "owner",
        colId: "owner",
        width: 200,
        minWidth: 180,
        resizable: true,
        cellClass: "truncate",
        cellRenderer: (params: ICellRendererParams<Property>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <p className="text-content-500 font-medium truncate">
              {row.owner || "--"}
            </p>
          );
        }
      });

    if (type.isPMS)
      ret.push({
        headerName: "Source",
        field: "pms_id",
        colId: "pms_id",
        width: type.isMultiPMS ? 200 : 135,
        minWidth: type.isMultiPMS ? 200 : 135,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Property>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <p className="font-medium text-content-500 truncate">
              {getSourceLabel(row)}
            </p>
          );
        }
      });

    ret.push({
      headerName: "",
      field: "id",
      colId: "actions",
      width: 72,
      minWidth: 72,
      pinned: "right",
      suppressMovable: true,
      resizable: false,
      cellRenderer: (params: ICellRendererParams<Property>) => {
        const property = params.data;
        if (!property) return null;

        const popoverActions: Action[] = [
          {
            title: "Review",
            onClick: () => navigate(`/properties/${property.id}`)
          }
        ];

        if (type.isBoomScreen)
          if (type.isBoomCRM)
            popoverActions.push({
              title: `Copy ${propertyDictionary} listing page`,
              onClick: async (e) => {
                e.stopPropagation();

                handleCopy(property.listing_url || "", "Link copied");
              }
            });
          else
            popoverActions.push({
              title: `Copy link to ${propertyDictionary}`,
              onClick: async (e) => {
                e.stopPropagation();

                try {
                  await navigator.clipboard.writeText(
                    property.magic_link?.application_link || ""
                  );

                  toast.info("Link copied");
                } catch (err) {
                  toast.error(getErrorMessage(err));
                }
              },
              disabled: !property.total_units,
              tooltip: `You can't copy a link to a ${propertyDictionary} without ${units}`
            });

        popoverActions.push({
          title: `Delete ${propertyDictionary}`,
          color:
            property.can_delete && !isBoomscreenAndPms
              ? "text-danger-500"
              : "text-content-100",
          onClick: (e) => {
            e.stopPropagation();
            if (property.can_delete && !isBoomscreenAndPms) {
              setShowDeletePropertyModal(true);
              setSelectedPropertyId(String(property.id));
            }
          },
          disabled: !property.can_delete || isBoomscreenAndPms,
          tooltip:
            type.isPMS && type.isBoomScreen
              ? `You can't delete a ${propertyDictionary}`
              : `You can't delete a ${propertyDictionary} with applications`
        });

        return (
          <ContextMenuPopover
            placement="bottom-start"
            actions={popoverActions}
            triggerClassName="stroke-content-500"
          />
        );
      }
    });

    return ret;
  }, [
    type.isBoomScreen,
    type.isPMS,
    type.isMultiPMS,
    type.isBoomCRM,
    hasPropertyOwner,
    showPropertyGroup,
    Property,
    Units,
    Unit,
    propertyDictionary,
    units,
    setShowDeletePropertyModal,
    setSelectedPropertyId,
    navigate
  ]);

  const getRowId = useCallback(
    (params: GetRowIdParams<Property>) => String(params.data.id),
    []
  );

  const onCellClicked = useCallback(
    (event: CellClickedEvent<Property>) => {
      const row = event.data;
      if (!row) return;
      if (event.column.getColId() === "actions") return;
      navigate(`/properties/${row.id}`);
    },
    [navigate]
  );

  return (
    <AgGrid<Property>
      themeOverrides={{ "--ag-cell-horizontal-padding": "16px" }}
      columns={columns}
      rows={data}
      loading={isLoading}
      footer={pagination}
      showFooter={!!pagination}
      perPageRows={perPageRows}
      viewPortHeight={sectionHeight}
      viewPortHeightOffset={
        viewPortHeightOffset ?? DEFAULT_AG_GRID_VIEWPORT_OFFSET
      }
      getRowId={getRowId}
      onCellClicked={onCellClicked}
      noRowsFallback={
        <NoRowsFallback
          Icon={Search01Icon}
          title={
            hasSearchParams
              ? "No results found"
              : `${Properties} will appear here`
          }
          text={
            hasSearchParams
              ? "We couldn't find any data matching your query"
              : `Add ${propertyDictionary} to be able to invite applicants for screening`
          }
        />
      }
    />
  );
};
